/**
 * Fonte única de verdade dos regimes de conta (tabela account_regimes).
 *
 * Este módulo é intencionalmente SEM DEPENDÊNCIAS: utils.js importa controllers, e os controllers
 * precisam dos regimes, então importar utils.js aqui criaria ciclo. A normalização de acento é
 * feita inline via normalize('NFD') em vez de reusar removeAccents.
 *
 * IMPORTANTE — duas codificações de "consolidado" convivem no sistema:
 *   - CONSOLIDATED_SELECT_VALUE (0) nos MySelect (<option value={0}>)
 *   - CONSOLIDATED_TOGGLE_VALUE (null) no MyToggleSegregationButton
 * As duas chegam ao backend como SQL NULL:
 *   client/src/API/client.js -> accountRegimeId ? accountRegimeId : 0
 *   src/controllers/client.controller.js -> parseInt(account_regime_id) != 0 ? ... : null
 * e toda query filtra com (acc.account_regime_id = $N OR $N IS NULL). Não há bug a corrigir aqui:
 * ambas são falsy em JS, então os guards `if (accountRegimeId)` e `== 0` funcionam para as duas.
 */

export const CONSOLIDATED_SELECT_VALUE = 0;
export const CONSOLIDATED_TOGGLE_VALUE = null;
export const CONSOLIDATED_LABEL = 'Consolidada';

/** Ids dos regimes, para não espalhar literais pelo código. */
export const ACCOUNT_REGIME = {
    CONSOLIDATED: 0,
    PREVIDENCIARIO: 1,
    FINANCEIRO: 2,
    TAXA_ADMINISTRATIVA: 3,
    MILITAR: 4,
    APORTE: 5,
    FOR: 6,
};

/**
 * Definição de cada regime.
 *
 * label      -> rótulo das listas de opção (MySelect / options de relatório)
 * shortLabel -> rótulo do toggle de segregação, onde o espaço é apertado
 * upperLabel -> igual a account_regimes.description; é o que sai nas planilhas
 * sheetLabel -> nome de aba de Excel: sem acento e dentro do limite de 31 chars do XLSX
 * clientFlag -> coluna booleana de clients que libera o regime; null = sempre liberado
 * aliases    -> variações aceitas na importação por planilha
 */
export const ACCOUNT_REGIMES = [
    {
        id: ACCOUNT_REGIME.PREVIDENCIARIO,
        label: 'Previdenciário',
        shortLabel: 'Previdenciário',
        upperLabel: 'PREVIDENCIÁRIO',
        sheetLabel: 'PREVIDENCIARIO',
        clientFlag: null,
        aliases: ['PREVIDENCIARIO', 'PREVIDENCIARIA'],
    },
    {
        id: ACCOUNT_REGIME.FINANCEIRO,
        label: 'Financeiro',
        shortLabel: 'Financeiro',
        upperLabel: 'FINANCEIRO',
        sheetLabel: 'FINANCEIRO',
        clientFlag: 'portfolio_segregated',
        aliases: ['FINANCEIRO', 'FINANCEIRA'],
    },
    {
        id: ACCOUNT_REGIME.TAXA_ADMINISTRATIVA,
        label: 'Taxa de Administração',
        shortLabel: 'Taxa de Adm.',
        upperLabel: 'TAXA DE ADMINISTRAÇÃO',
        sheetLabel: 'TAXA DE ADMINISTRACAO',
        clientFlag: null,
        aliases: ['TAXA ADMINISTRATIVA', 'TAXA ADM', 'TAXA ADM.', 'TAXA DE ADMINISTRACAO'],
    },
    {
        id: ACCOUNT_REGIME.MILITAR,
        label: 'Militar',
        shortLabel: 'Militar',
        upperLabel: 'MILITAR',
        sheetLabel: 'MILITAR',
        clientFlag: 'is_military_segregation',
        aliases: ['MILITAR'],
    },
    {
        id: ACCOUNT_REGIME.APORTE,
        label: 'Aporte',
        shortLabel: 'Aporte',
        upperLabel: 'APORTE',
        sheetLabel: 'APORTE',
        clientFlag: 'is_aporte_for_segregation',
        aliases: ['APORTE', 'APORTES'],
    },
    {
        id: ACCOUNT_REGIME.FOR,
        label: 'Fundo de Oscilação de Risco',
        shortLabel: 'FOR',
        upperLabel: 'FUNDO DE OSCILAÇÃO DE RISCO',
        sheetLabel: 'OSCILACAO DE RISCO',
        clientFlag: 'is_aporte_for_segregation',
        aliases: ['FUNDO DE OSCILACAO DE RISCO', 'OSCILACAO DE RISCO', 'FOR'],
    },
];

/** Todos os ids, para laços de cálculo que não têm o cliente em escopo. */
export const ALL_REGIME_IDS = ACCOUNT_REGIMES.map(regime => regime.id);

/** id -> rótulo longo. */
export const REGIME_LABELS = ACCOUNT_REGIMES.reduce((acc, regime) => {
    acc[regime.id] = regime.label;
    return acc;
}, {});

/** id -> rótulo MAIÚSCULO, igual ao description do banco. Usado nas planilhas. */
export const REGIME_UPPER_LABELS = ACCOUNT_REGIMES.reduce((acc, regime) => {
    acc[regime.id] = regime.upperLabel;
    return acc;
}, {});

/** id -> nome de aba de Excel. */
export const REGIME_SHEET_LABELS = ACCOUNT_REGIMES.reduce((acc, regime) => {
    acc[regime.id] = regime.sheetLabel;
    return acc;
}, {});

/**
 * Aceita tanto o clientOn ({ client }) quanto o client cru, porque os dois formatos circulam:
 * as telas passam clientOn e o ReportController monta { client: client } na mão.
 * @param {Object} clientOrClientOn
 * @returns {Object|null}
 */
function resolveClient(clientOrClientOn) {

    if (!clientOrClientOn) return null;

    if (Object.prototype.hasOwnProperty.call(clientOrClientOn, 'client')) {
        return clientOrClientOn.client || null;
    }

    return clientOrClientOn;
}

/**
 * Verifica se um regime está liberado para o cliente.
 * @param {Object} clientOrClientOn
 * @param {Number|String} regimeId
 * @returns {Boolean}
 */
export function isRegimeEnabled(clientOrClientOn, regimeId) {

    const client = resolveClient(clientOrClientOn);
    if (!client) return false;

    const regime = ACCOUNT_REGIMES.find(el => el.id === parseInt(regimeId));
    if (!regime) return false;

    return !regime.clientFlag || !!client[regime.clientFlag];
}

/**
 * Regimes liberados para o cliente, objetos completos.
 * @param {Object} clientOrClientOn
 * @returns {Array}
 */
export function getActiveRegimes(clientOrClientOn) {

    const client = resolveClient(clientOrClientOn);
    if (!client) return [];

    return ACCOUNT_REGIMES.filter(regime => !regime.clientFlag || !!client[regime.clientFlag]);
}

/**
 * Ids dos regimes liberados para o cliente. Substitui os laços literais [1, 2, 3, 4] nos pontos
 * em que o cliente está em escopo, evitando requisição por regime que ele não tem.
 * @param {Object} clientOrClientOn
 * @returns {Array<Number>}
 */
export function getActiveRegimeIds(clientOrClientOn) {
    return getActiveRegimes(clientOrClientOn).map(regime => regime.id);
}

/**
 * Opções { name, value } no formato que os MySelect do sistema esperam.
 * @param {Object}  clientOrClientOn
 * @param {Object}  [options]
 * @param {Boolean} [options.includeConsolidated=false] inclui a entrada consolidada com value 0
 * @param {String}  [options.consolidatedLabel] rótulo da entrada consolidada ('Consolidado' em
 *                                              algumas telas, 'Consolidada' em outras)
 * @param {Array}   [options.extraIds=[]] ids exibidos mesmo sem a flag do cliente; usado nos
 *                                        fluxos de importação, em que a origem pode ter conta de
 *                                        um regime que o destino não habilita
 * @returns {Array<{name: String, value: Number}>}
 */
export function getRegimeOptions(clientOrClientOn, options = {}) {

    const {
        includeConsolidated = false,
        consolidatedLabel = CONSOLIDATED_LABEL,
        extraIds = [],
    } = options;

    const client = resolveClient(clientOrClientOn);
    if (!client) return [];

    const regimeOptions = ACCOUNT_REGIMES
        .filter(regime => !regime.clientFlag
            || !!client[regime.clientFlag]
            || extraIds.includes(regime.id))
        .map(regime => ({ name: regime.label, value: regime.id }));

    if (includeConsolidated) {
        regimeOptions.unshift({ name: consolidatedLabel, value: CONSOLIDATED_SELECT_VALUE });
    }

    return regimeOptions;
}

/**
 * Rótulo de um regime. Trata 0, null, undefined e id desconhecido como consolidado, de modo que
 * nunca retorna undefined (a capa de relatório imprime esse valor direto).
 * @param {Number|String} regimeId
 * @param {String} [consolidatedLabel='Consolidado']
 * @returns {String}
 */
export function getRegimeLabel(regimeId, consolidatedLabel = 'Consolidado') {

    const id = parseInt(regimeId);
    if (!id) return consolidatedLabel;

    const regime = ACCOUNT_REGIMES.find(el => el.id === id);
    return regime ? regime.label : consolidatedLabel;
}

/**
 * MAIÚSCULO, sem acento, espaços colapsados. Local para não depender de utils.js.
 * @param {String} value
 * @returns {String}
 */
function normalizeRegimeText(value) {

    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Descrição -> id, ignorando caixa e acento e aceitando os apelidos de cada regime (inclusive
 * 'FOR'). Usado na importação de carteira por planilha.
 * @param {String} description
 * @returns {Number|undefined} undefined quando não reconhecido, deixando o default no chamador
 */
export function getRegimeIdByDescription(description) {

    const needle = normalizeRegimeText(description);
    if (!needle) return undefined;

    const regime = ACCOUNT_REGIMES.find(el =>
        normalizeRegimeText(el.label) === needle
        || normalizeRegimeText(el.upperLabel) === needle
        || el.aliases.some(alias => normalizeRegimeText(alias) === needle));

    return regime ? regime.id : undefined;
}

/**
 * Converte valor vindo de um MySelect em id de regime ou null.
 *
 * O MySelect renderiza um NativeSelect com <option>, então e.target.value é SEMPRE string — e a
 * string '0' é truthy. Use esta função em vez de checar o valor cru.
 * @param {Number|String} value
 * @returns {Number|null} null para consolidado
 */
export function parseRegimeValue(value) {
    const id = parseInt(value);
    return id ? id : null;
}

/**
 * True para 0, '0', null, undefined e ''.
 * @param {Number|String} value
 * @returns {Boolean}
 */
export function isConsolidated(value) {
    return !parseRegimeValue(value);
}
